import type { DragEvent } from "react";
import { useState } from "react";

const TASK_DRAG_MIME = "application/x-task-id";

export function useTaskDragSource(taskId: string, enabled: boolean) {
  return {
    draggable: enabled,
    onDragStart(event: DragEvent) {
      if (!enabled) return;
      event.dataTransfer.setData(TASK_DRAG_MIME, taskId);
      event.dataTransfer.effectAllowed = "move";
    }
  };
}

function computeDropEdge(event: DragEvent): "before" | "after" {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const midpoint = rect.top + rect.height / 2;
  return event.clientY < midpoint ? "before" : "after";
}

// Plain (non-hook) builder so it can be called per-row inside a .map() loop
// without violating the Rules of Hooks - the drag-over state it needs must be
// owned and passed in by the caller instead of via an internal useState.
export function buildTaskReorderTargetProps(
  setDragOverEdge: (edge: "before" | "after" | null) => void,
  onDrop: (draggedTaskId: string, edge: "before" | "after") => void,
  enabled: boolean
) {
  return {
    onDragOver(event: DragEvent) {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setDragOverEdge(computeDropEdge(event));
    },
    onDragEnter(event: DragEvent) {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
    },
    onDragLeave(event: DragEvent) {
      if (!enabled) return;
      event.stopPropagation();
      setDragOverEdge(null);
    },
    onDrop(event: DragEvent) {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      const edge = computeDropEdge(event);
      setDragOverEdge(null);
      const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
      if (taskId) onDrop(taskId, edge);
    }
  };
}

export function useTaskReorderTarget(onDrop: (draggedTaskId: string, edge: "before" | "after") => void, enabled: boolean) {
  const [dragOverEdge, setDragOverEdge] = useState<"before" | "after" | null>(null);

  return {
    dragOverEdge,
    reorderTargetProps: buildTaskReorderTargetProps(setDragOverEdge, onDrop, enabled)
  };
}

function computeDropZone(event: DragEvent): "before" | "middle" | "after" {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const ratio = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0.5;
  if (ratio < 0.25) return "before";
  if (ratio > 0.75) return "after";
  return "middle";
}

// Same idea as buildTaskReorderTargetProps, but splits each row into three
// drop zones so dropping in the middle can mean "nest as a subtask of this
// row" instead of only reordering before/after it.
export function buildTaskNestOrReorderTargetProps(
  setDragOverZone: (zone: "before" | "middle" | "after" | null) => void,
  onDrop: (draggedTaskId: string, zone: "before" | "middle" | "after") => void,
  enabled: boolean
) {
  return {
    onDragOver(event: DragEvent) {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setDragOverZone(computeDropZone(event));
    },
    onDragEnter(event: DragEvent) {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
    },
    onDragLeave(event: DragEvent) {
      if (!enabled) return;
      event.stopPropagation();
      setDragOverZone(null);
    },
    onDrop(event: DragEvent) {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      const zone = computeDropZone(event);
      setDragOverZone(null);
      const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
      if (taskId) onDrop(taskId, zone);
    }
  };
}

export function useTaskDropTarget(onDropTaskId: (taskId: string) => void, enabled: boolean) {
  const [isDragOver, setIsDragOver] = useState(false);

  return {
    isDragOver,
    dropTargetProps: {
      onDragOver(event: DragEvent) {
        if (!enabled) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      },
      onDragEnter(event: DragEvent) {
        if (!enabled) return;
        event.preventDefault();
        setIsDragOver(true);
      },
      onDragLeave() {
        if (!enabled) return;
        setIsDragOver(false);
      },
      onDrop(event: DragEvent) {
        if (!enabled) return;
        event.preventDefault();
        setIsDragOver(false);
        const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
        if (taskId) onDropTaskId(taskId);
      }
    }
  };
}
